import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getIsProductCompareFull,
    getProductCompareChecked,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import addProductToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/AddProductToCompare";
import removeProductFromCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button from "@solveindustrial/MobiusOverrides/Button/Button";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showPrice: boolean;
    showAddToList: boolean;

    /* Verndale Custom Feature */
    showCompare?: boolean;
}

const mapStateToProps = (state: ApplicationState, props: HasProductContext) => {
    const session = state.context.session;
    return {
        productSettings: getSettingsCollection(state).productSettings,

        /* Verndale Custom Feature - START */
        settingsCollection: getSettingsCollection(state),
        productCompareChecked: getProductCompareChecked(state, props.productContext),
        isProductCompareFull: getIsProductCompareFull(state),
        isAuthenticated: session && (session.isAuthenticated || session.rememberMe) && !session.isGuest,
        viewMode: state.pages.productList.view,
        /* Verndale Custom Feature - END */
    };
};

const mapDispatchToProps = {
    /* Verndale Custom Feature - START */
    addProductToCompare,
    removeProductFromCompare,
    /* Verndale Custom Feature - END */
};

type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

export interface ProductListActionsStyles {
    wrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    addToListLink?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;

    /* Verndale Custom Feature */
    bottomSection?: InjectableCss;
    compareCheckbox?: CheckboxPresentationProps;
    learnMoreButton?: ButtonPresentationProps;
    /* Verndale Custom Feature - END */
}

export const actionsStyles: ProductListActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
        `,
    },
    unitOfMeasureSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 20px;
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: {
            css: css`
                margin: 8px 0;
            `,
        },
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            margin-top: 10px;
        `,
    },
    addToCartButton: {
        css: css`
            width: 80%;
        `,
    },
    quantityOrdered: {
        cssOverrides: {
            formField: css`
                margin-right: 10px;
                width: 4.1rem;
                min-width: 4.1rem;
            `,
        },
    },
    addToListWrapper: {
        css: css`
            margin-top: 20px;
            min-height: 2rem;
        `,
    },
};

const styles = actionsStyles;

const ProductListActions: FC<Props> = ({
    productContext,
    productSettings,
    showPrice,
    showAddToList,

    /* Verndale Custom Feature - START */
    showCompare,
    isProductCompareFull,
    isAuthenticated,
    settingsCollection,
    productCompareChecked,
    addProductToCompare,
    removeProductFromCompare,
    viewMode,
    /* Verndale Custom Feature - END */
}) => {
    /* Verndale Custom Feature - START */
    const toasterContext = useContext(ToasterContext);

    const handleChanged = (_: React.SyntheticEvent, value: boolean) => {
        if (value && isProductCompareFull) {
            toasterContext.addToast({
                body: siteMessage("ProductionCompare_MaximumItemsReached"),
                messageType: "warning",
            });
        } else if (value) {
            addProductToCompare({ productId: productContext.product.id });
        } else {
            removeProductFromCompare({ productId: productContext.product.id });
        }
    };

    // Verndale Custom Feature - START
    if (!isAuthenticated) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.addToCartWrapper}>
                    {/* Verndale Custom Feature - START */}
                    {viewMode === "List" && (
                        <Button
                            {...styles.learnMoreButton}
                            data-test-selector="productList_LearnMore"
                            onClick={() => {
                                // redirect to the product detail page
                                window.location.href = productContext.productInfo.productDetailPath;
                            }}
                        >
                            {translate("Learn More")}
                        </Button>
                    )}
                    {/* Verndale Custom Feature - END */}
                </StyledWrapper>

                <StyledWrapper {...styles.bottomSection}>
                    {showCompare && settingsCollection.productSettings.enableProductComparisons && (
                        <Checkbox
                            {...styles.compareCheckbox}
                            checked={productCompareChecked}
                            onChange={handleChanged}
                            data-test-selector="productList_CompareCheckbox"
                        >
                            {translate("Compare")}
                        </Checkbox>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        );
    }
    /* Verndale Custom Feature - END */

    return (
        <StyledWrapper {...styles.wrapper}>
            {showPrice && (
                <>
                    <ProductPrice
                        product={productContext}
                        showLabel={false}
                        showSavings={true}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                        extendedStyles={styles.price}
                    />
                    <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                </>
            )}
            <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
            <StyledWrapper {...styles.addToCartWrapper}>
                <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} />
                <ProductAddToCartButton
                    data-test-selector={`actionsAddToCart${productContext.product.id}`}
                    extendedStyles={styles.addToCartButton}
                />
            </StyledWrapper>

            {/* Verndale Custom Feature - START */}
            <StyledWrapper {...styles.bottomSection}>
                {showCompare && settingsCollection.productSettings.enableProductComparisons && (
                    <Checkbox
                        {...styles.compareCheckbox}
                        checked={productCompareChecked}
                        onChange={handleChanged}
                        data-test-selector="productList_CompareCheckbox"
                    >
                        {translate("Compare")}
                    </Checkbox>
                )}

                {showAddToList && (
                    <StyledWrapper {...styles.addToListWrapper}>
                        <ProductAddToListLink
                            data-test-selector={`actionsAddToList${productContext.product.id}`}
                            extendedStyles={styles.addToListLink}
                        />
                    </StyledWrapper>
                )}
            </StyledWrapper>
            {/* Verndale Custom Feature - END */}
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductListActions));
